import { useContext, useEffect, useState } from 'react';
import { Button, createTheme, Modal, ThemeProvider, Typography } from '@mui/material';
import { Box } from '@mui/system';
import GlobalContext from 'contexts/GlobalContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useAppDispatch, useAppSelector } from 'hooks';
import { removeTable } from 'reducers/tables';
import useGameService from 'services/GameService';
import { markRecentlyExitedTable } from 'utils/recentlyExitedTables';

const DuplicateScreenModal = () => {
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const {
    isDuplicateAccess,
    duplicateAccessMsg,
    duplicateAccessGameID,
    duplicateAccessGame,
    setIsDuplicateAccess
  } = useContext(GlobalContext);
  const { returnToLobby } = useContext(ViewManagerContext);
  const { unsubscribeTable } = useGameService();
  const dispatch = useAppDispatch();
  // Count of every table this connection is subscribed to (seated + observed).
  // Used to decide whether removing the duplicate leaves the user with
  // anything to see — if so we stay put, otherwise we navigate to lobby.
  const openTableCount = useAppSelector((state) => Object.keys((state as any).tables || {}).length);
  // The duplicate table's own game type, for an unsubscribe whose payload
  // omits it: the server keys the subscription by game type + gameID, so an
  // Omaha table unsubscribed as Texas would keep holding a slot it counts.
  const duplicateTableGame = useAppSelector(
    (state) => (state as any).tables?.[String(duplicateAccessGameID || '')]?.game?.game
  ) as string | undefined;
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (isDuplicateAccess) {
      setMsg(duplicateAccessMsg);
      setShowDuplicateModal(true);
    } else {
      setMsg(duplicateAccessMsg);
      setShowDuplicateModal(false);
    }
  }, [isDuplicateAccess]);

  const handleExitTable = () => {
    // Tear down ONLY the table the engine flagged as duplicate (engine
    // ships gameID/game in the GOT_DUPLICATE_ACCESS payload — see
    // apps/engine/messages/inbound.js). Grid view keeps the other tables
    // open; single-table view falls through to returnToLobby so the user
    // isn't left staring at a now-stale table.
    //
    // markRecentlyExitedTable blocks App.tsx's GOT_TEXAS / GOT_PLAYER_GAMES
    // auto-resub from re-hydrating the slot inside the grace window;
    // removeTable drops the slice so the TableHeader watcher chip clears
    // immediately rather than waiting for the next GOT_PLAYER_GAMES push.
    const exitGameID = String(duplicateAccessGameID || '');
    if (exitGameID) {
      markRecentlyExitedTable(exitGameID);
      unsubscribeTable(exitGameID, duplicateAccessGame || duplicateTableGame || 'texas');
      dispatch(removeTable(exitGameID));
    }

    // Reset the duplicate-access flag so the modal closes; otherwise the next
    // table the user opens would re-trigger it from stale context state.
    setIsDuplicateAccess(false);

    // Decide where to send the user after the modal closes. `openTableCount`
    // is the snapshot BEFORE removeTable dispatched above, so we compare
    // against 1 (the duplicate we just removed) rather than 0. If there are
    // sibling tables in grid view, stay put — MultiTableGrid will re-layout
    // the remaining cells. Single-table view (openTableCount === 1) hits
    // returnToLobby so the user isn't left staring at the route of a table
    // that no longer exists in state.tables.
    if (openTableCount <= 1) {
      returnToLobby();
    }
  };

  return (
    <Modal open={showDuplicateModal}>
      <Box sx={modalStyle}>
        <Typography
          id='modal-modal-title'
          sx={{ textAlign: 'center', marginBottom: '1rem', fontWeight: 'bold' }}>
          {msg}
        </Typography>
        <div
          style={{
            justifyContent: 'center',
            marginTop: '2rem',
            gap: '2rem'
          }}>
          <ThemeProvider theme={UnregisterBtnStyle}>
            <Button
              sx={{ display: 'block', opacity: '.8' }}
              variant='contained'
              onClick={handleExitTable}>
              EXIT TABLE
            </Button>
          </ThemeProvider>
        </div>
      </Box>
    </Modal>
  );
};

export default DuplicateScreenModal;

const modalStyle = {
  position: 'absolute' as const,
  top: '40%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: '#12100c',
  color: 'white',
  padding: '2rem',
  width: 'min(90vw, 360px)',
  borderRadius: '12px',
  border: '1px solid rgba(212, 168, 83, 0.2)'
};

const UnregisterBtnStyle = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          color: 'white',
          backgroundColor: 'rgba(212, 168, 83, 0.2)',
          '&:hover': {
            color: 'white',
            backgroundColor: 'rgba(212, 168, 83, 0.3)'
          },
          '&:disabled': {
            color: 'white',
            opacity: '0.4'
          },
          fontSize: '1rem',
          fontWeight: '400',
          padding: '0.5rem 1rem',
          minWidth: 'unset',
          textTransform: 'none',
          width: '100%',
          marginTop: '10px'
        }
      }
    }
  }
});
